<template>
  <button
    class="color-swatch no-focus"
    :data-color="color && capitalize(color)"
    :class="classes"
  >
    <span
      v-if="tooltip"
      class="color-swatch__tooltip body-2"
      v-html="color && capitalize(color)"
    />

    <span
      class="color-swatch__swatch no-focus"
      :class="{
        [whiteSwatchClass]: isWhiteSwatch
      }"
    >
      <span class="visually-hidden">
        {{ product.title }}
      </span>

      <span
        v-if="color"
        ref="innerSwatch"
        :style="styles"
        class="color-swatch__swatch-inner"
      >
        <img
          v-if="color"
          :alt="color"
          :src="swatchImageUrl"
          onload="this.style.display = 'block'"
          style="display: none;"
        >
      </span>
    </span>
  </button>
</template>

<script>

/**
 * Vue: Color swatch (color-swatch)
 * -----------------------------------------------------------------------------
 * Color Swatch.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [color] - Color of the swatch.
 * @param {Boolean} displayOutOfStock - Enable strikethrough on out of stock
 * colors.
 * @param {Object|Boolean} [product] - Product object.
 * @param {Boolean} [isActive] - Set active state on swatch.
 * @param {Array|Boolean} [images] - Images associated with the swatch.
 * @param {String} swatchImage - The swatch image to use for this swatch,
 * otherwise defaults to finding the color from Shopify files CDN.
 * @param {Boolean} tooltip - Enable tooltip.
 *
 */
import { capitalize } from '~/helpers/general'
import { isVariantOptionAvailable } from '~/helpers/product'

export default {
  name: 'ColorSwatch',

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    displayOutOfStock: {
      type: Boolean,
      default: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    images: {
      type: [Boolean, Array],
      default: () => [],
    },
    swatchImage: {
      type: String,
      default: '',
    },
    tooltip: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      global: true,
      capitalize,
      isVariantOptionAvailable,
      whiteSwatchClass: 'color-swatch__swatch--white',
    }
  },

  computed: {

    /**
     * Returns the dynamic classes.
     * @returns {Object}
     */
    classes() {
      return {
        'is-active': this.isActive,
        'has-swatch-image': this.swatchImageUrl || false,
        'is-disabled': !this.isVariantOptionAvailable(this.product, this.color) && this.displayOutOfStock,
      }
    },

    /**
     * Compute if the swatch is considered white
     * @returns {boolean}
     */
    isWhiteSwatch() {
      if (!this.color) {
        return true
      }
      const whiteSwatches = this.$variable('product.whiteSwatches')
      return whiteSwatches.indexOf(this.color.toLowerCase()) > -1
    },

    /**
     * Returns the dynamic styles.
     * @returns {Object}
     */
    styles() {
      if (!this.swatchImageUrl) {
        return {
          backgroundColor: this.color.replace(' ', '').toLowerCase(),
          color: this.color.replace(' ', '').toLowerCase(),
        }
      }

      return {}
    },

    /**
     * Returns the URL for the swatch image.
     * @returns {String}
     */
    swatchImageUrl() {
      if (this.swatchImage) {
        return this.swatchImage
      }

      const { images } = this.product
      const swatchImage = images.findIndex((image) => image?.alt?.includes('Swatch ' + this.color))

      if (swatchImage !== -1) {
        return images[swatchImage].src
      }

      const cdnVariantName = this.color.toLowerCase().replace(' ', '-').replace('/', '-')
      const cdnSwatchImage = `https://cdn.shopify.com/s/files/1/0607/1844/8811/files/swatch_${cdnVariantName}.jpg`

      if (cdnSwatchImage) {
        return cdnSwatchImage
      }

      return false
    },
  },

  mounted() {
    this.handleInvalidBackgrounds()
  },

  methods: {

    /**
     * Add a gray border to backgrounds that don't have a background color or
     * image.
     */
    handleInvalidBackgrounds() {
      const background = this.$refs.innerSwatch.style.backgroundColor
      if (!background && !this.swatchImageUrl) {
        this.$refs.innerSwatch.classList.add(this.whiteSwatchClass)
      }
    },
  },
}
</script>
